/**
 * Message Sequence Types (DAG-based)
 *
 * Types for automated message sequences with branching logic.
 * Sequences are DAG trees: each step can have multiple children
 * selected by evaluating branch conditions.
 */

import type { UUID, BaseEntity } from "./common.types";
import type { QueryCriteria } from "./list.types";

// =====================================================
// STEP TYPES
// =====================================================

export type StepType =
  | "send_email"
  | "send_sms"
  | "apply_tag"
  | "remove_tag"
  | "wait"
  | "add_to_list"
  | "remove_from_list"
  | "update_attribute"
  | "create_task";

export type BranchCondition =
  | "default"
  | "email_opened"
  | "email_not_opened"
  | "link_clicked"
  | "link_not_clicked"
  | "form_submitted"
  | "tag_applied"
  | "tag_not_applied"
  | "contact_field_equals"
  | "contact_field_not_equals"
  | "in_segment"
  | "not_in_segment";

export type EnrollmentPhase =
  | "pending_action"
  | "waiting_evaluation"
  | "pending_branch"
  // Phase 2A: enrollment hit an evaluation_error in a branch condition
  // (cycle / cross-org / missing segment / un-evaluable criteria) and
  // is held with exponential backoff. Operator resolves via the resolve
  // RPC; Phase 0 in process-sequence-steps rehydrates on next tick.
  | "held_evaluation";

// =====================================================
// SEQUENCE TYPES
// =====================================================

export type SequenceTriggerType =
  | "manual"
  | "list_membership"
  | "event_registration"
  | "event_attended"
  | "member_expiration"
  | "product_acquired"
  | "member_signup"
  | "reinstated_member"
  | "contact_created";

export type SequenceStatus = "draft" | "active" | "paused" | "archived";

export type EvaluationCadence =
  | "immediate"
  | "hourly"
  | "daily"
  | "weekly"
  | "manual";

export interface TriggerConfig {
  product?: string;
  audience?: "primary" | "secondary" | "both";
  days_before?: number;
  days_after?: number;
  list_id?: UUID;
  event_id?: UUID;
}

export interface MessageSequence extends BaseEntity {
  name: string;
  description?: string;
  trigger_type: SequenceTriggerType;
  trigger_config: TriggerConfig;
  trigger_conditions?: QueryCriteria | null;
  trigger_date_field?: string | null;
  trigger_offset_days?: number;
  event_id?: UUID | null;
  event?: {
    id: UUID;
    title: string;
    start_date: string;
  } | null;
  evaluation_cadence?: EvaluationCadence;
  next_evaluation_at?: string | null;
  last_evaluated_at?: string | null;
  list_id?: UUID;
  subscription_group_id?: UUID | null;
  list?: {
    id: UUID;
    name: string;
    contact_count: number;
  };
  status: SequenceStatus;
  version: number;
  campaign_id?: UUID;
  campaign?: {
    id: UUID;
    name: string;
  };
  metadata?: Record<string, unknown>;
  /** Pardot parity Tier 1 (2026-05-17). When true, contacts whose prior
   *  enrollment in this sequence is completed/cancelled can be re-enrolled.
   *  Active/paused enrollments still block regardless of this flag. */
  allow_reenrollment?: boolean;
  created_by?: UUID;
  updated_by?: UUID;
}

// =====================================================
// SEQUENCE STEP TYPES (DAG)
// =====================================================

export type DelayUnit = "minutes" | "hours" | "days" | "weeks";

/** Pardot parity Tier 1 (2026-05-17). */
export type DelayMode = "relative" | "absolute_date" | "relative_to_field";

export type ConditionLogic = "and" | "or";

export interface BranchConditionConfig {
  tag?: string;
  field?: string;
  value?: string;
  link_url?: string;
  form_id?: string;
  segment_id?: string;
  condition_logic?: ConditionLogic;
}

export interface SendEmailConfig {
  email_subject?: string;
  email_template_id?: string;
  email_template_data?: Record<string, unknown>;
  /** mig 1550: '{{assigned_agent}}' or absent (absent = U.S. LawShield noreply). */
  from_email?: string;
  /** mig 1550: a plain address, '{{assigned_agent}}', or absent. */
  reply_to?: string;
  /** mig 1550: plain address used when reply_to is the variable and the agent can't be used. */
  reply_to_fallback?: string;
  /** 2026-11-04: display name for the U.S. LawShield From (fixed or fallback; an agent keeps their own name). */
  from_name?: string;
  /** 2026-11-04: display name for a fixed or fallback Reply-To (an agent keeps their own name). */
  reply_to_name?: string;
}

export interface SendSmsConfig {
  sms_template_id?: UUID;
  sms_message_override?: string;
}

export interface ApplyTagConfig {
  tag: string;
}

export interface RemoveTagConfig {
  tag: string;
}

export interface AddToListConfig {
  list_id: UUID;
}

export interface RemoveFromListConfig {
  list_id: UUID;
}

export interface UpdateAttributeConfig {
  field: string;
  value: string;
}

export interface CreateTaskConfig {
  task_type?: string;
  notes?: string;
  agent_name?: string;
  due_date_offset_days?: number;
}

export type ActionConfig =
  | SendEmailConfig
  | SendSmsConfig
  | ApplyTagConfig
  | RemoveTagConfig
  | AddToListConfig
  | RemoveFromListConfig
  | UpdateAttributeConfig
  | CreateTaskConfig
  | Record<string, unknown>;

export interface SequenceStep {
  id: UUID;
  sequence_id: UUID;
  parent_step_id: UUID | null;
  branch_condition: BranchCondition;
  branch_condition_config: BranchConditionConfig;
  branch_order: number;
  name: string | null;
  step_type: StepType;
  delay_value: number;
  delay_unit: DelayUnit;
  /** Pardot parity Tier 1 (2026-05-17). When 'absolute_date', uses
   *  delay_absolute_at. When 'relative_to_field', uses
   *  action_config.delay_relative_to_field. */
  delay_mode?: DelayMode;
  delay_absolute_at?: string | null;
  evaluation_wait_value: number;
  evaluation_wait_unit: DelayUnit;
  action_config: ActionConfig;
  include_voucher: boolean;
  voucher_template_id: UUID | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  depth?: number;
  children?: SequenceStep[];
}

// =====================================================
// ENROLLMENT TYPES
// =====================================================

export type EnrollmentStatus =
  | "active"
  | "paused"
  | "completed"
  | "cancelled"
  | "failed";

export interface SequenceEnrollment {
  id: UUID;
  sequence_id: UUID;
  sequence?: MessageSequence;
  contact_id: UUID;
  contact?: {
    id: UUID;
    first_name: string;
    last_name: string;
    email?: string;
    phone_number?: string;
  };
  current_step_id: UUID | null;
  current_step?: SequenceStep;
  phase: EnrollmentPhase;
  status: EnrollmentStatus;
  enrolled_at: string;
  next_action_at?: string;
  evaluation_wait_until?: string;
  completed_at?: string;
  cancelled_at?: string;
  trigger_source?: string;
  trigger_data?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  enrolled_by?: UUID;
  created_at: string;
  updated_at: string;
}

// =====================================================
// SCHEDULED MESSAGE TYPES (unchanged)
// =====================================================

export type ScheduledMessageSource = "sequence" | "flight" | "one_off";

export type ScheduledMessageStatus =
  | "pending"
  | "processing"
  | "sent"
  | "delivered"
  | "failed"
  | "cancelled";

export interface ScheduledMessage {
  id: UUID;
  source_type: ScheduledMessageSource;
  enrollment_id?: UUID;
  sequence_step_id?: UUID;
  flight_id?: UUID;
  contact_id: UUID;
  contact?: {
    id: UUID;
    first_name: string;
    last_name: string;
    email?: string;
    phone_number?: string;
  };
  channel: "email" | "sms";
  email_address?: string;
  email_subject?: string;
  email_template_id?: string;
  email_template_data?: Record<string, unknown>;
  email_body_html?: string;
  email_body_text?: string;
  phone_number?: string;
  sms_body?: string;
  voucher_id?: UUID;
  scheduled_for: string;
  priority: number;
  status: ScheduledMessageStatus;
  sent_at?: string;
  delivered_at?: string;
  failed_at?: string;
  failure_reason?: string;
  provider_message_id?: string;
  provider_response?: Record<string, unknown>;
  attempt_count: number;
  max_attempts: number;
  next_retry_at?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// =====================================================
// MESSAGE HISTORY TYPES
// =====================================================

export interface SequenceMessageHistory {
  id: UUID;
  enrollment_id: UUID;
  step_id: UUID;
  scheduled_message_id?: UUID;
  step_type?: StepType;
  channel?: string;
  status: string;
  action_result?: Record<string, unknown>;
  branch_chosen?: string;
  branch_decided_at?: string;
  email_opened_at?: string;
  email_clicked_at?: string;
  email_links_clicked?: string[];
  sent_at?: string;
  delivered_at?: string;
  failed_at?: string;
  failure_reason?: string;
  created_at: string;
}

// =====================================================
// CREATE/UPDATE TYPES
// =====================================================

export interface CreateSequenceInput {
  name: string;
  description?: string;
  trigger_type: SequenceTriggerType;
  trigger_config?: TriggerConfig;
  trigger_conditions?: QueryCriteria | null;
  trigger_date_field?: string | null;
  trigger_offset_days?: number;
  event_id?: UUID | null;
  evaluation_cadence?: EvaluationCadence;
  list_id?: UUID;
  subscription_group_id?: UUID | null;
  campaign_id?: UUID;
  status?: SequenceStatus;
  metadata?: Record<string, unknown> | null;
  allow_reenrollment?: boolean;
}

export interface UpdateSequenceInput {
  name?: string;
  description?: string;
  trigger_type?: SequenceTriggerType;
  trigger_config?: TriggerConfig;
  trigger_conditions?: QueryCriteria | null;
  trigger_date_field?: string | null;
  trigger_offset_days?: number;
  event_id?: UUID | null;
  evaluation_cadence?: EvaluationCadence;
  list_id?: UUID | null;
  subscription_group_id?: UUID | null;
  campaign_id?: UUID | null;
  status?: SequenceStatus;
  metadata?: Record<string, unknown> | null;
  allow_reenrollment?: boolean;
}

export interface CreateSequenceStepInput {
  sequence_id: UUID;
  parent_step_id?: UUID | null;
  branch_condition?: BranchCondition;
  branch_condition_config?: BranchConditionConfig;
  branch_order?: number;
  name?: string;
  step_type: StepType;
  delay_value?: number;
  delay_unit?: DelayUnit;
  delay_mode?: DelayMode;
  delay_absolute_at?: string | null;
  evaluation_wait_value?: number;
  evaluation_wait_unit?: DelayUnit;
  action_config?: ActionConfig;
  include_voucher?: boolean;
  voucher_template_id?: UUID;
  is_active?: boolean;
}

export interface UpdateSequenceStepInput {
  parent_step_id?: UUID | null;
  branch_condition?: BranchCondition;
  branch_condition_config?: BranchConditionConfig;
  branch_order?: number;
  name?: string;
  step_type?: StepType;
  delay_value?: number;
  delay_unit?: DelayUnit;
  delay_mode?: DelayMode;
  delay_absolute_at?: string | null;
  evaluation_wait_value?: number;
  evaluation_wait_unit?: DelayUnit;
  action_config?: ActionConfig;
  include_voucher?: boolean;
  voucher_template_id?: UUID | null;
  is_active?: boolean;
}

export interface EnrollContactInput {
  sequence_id: UUID;
  contact_id: UUID;
  trigger_source?: string;
  trigger_data?: Record<string, unknown>;
}

export interface BulkEnrollInput {
  sequence_id: UUID;
  contact_ids: UUID[];
  trigger_source?: string;
}

// =====================================================
// ANALYTICS TYPES
// =====================================================

export interface SequenceAnalytics {
  total_enrolled: number;
  active_count: number;
  completed_count: number;
  cancelled_count: number;
  messages_sent: number;
  messages_delivered: number;
  messages_failed: number;
  emails_opened: number;
  emails_clicked: number;
  /** Email steps only — the correct denominator for an open rate. */
  emails_sent: number;
}

export interface SequenceWithAnalytics extends MessageSequence {
  analytics?: SequenceAnalytics;
  steps?: SequenceStep[];
  active_enrollments?: number;
}

// =====================================================
// UI HELPER CONSTANTS
// =====================================================

export const STEP_TYPE_OPTIONS: Array<{
  value: StepType;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    value: "send_email",
    label: "Send Email",
    description: "Send an email using a SendGrid template",
    icon: "EnvelopeIcon",
  },
  {
    value: "send_sms",
    label: "Send SMS",
    description: "Send an SMS message",
    icon: "DevicePhoneMobileIcon",
  },
  {
    value: "apply_tag",
    label: "Apply Tag",
    description: "Add a tag to the contact",
    icon: "TagIcon",
  },
  {
    value: "remove_tag",
    label: "Remove Tag",
    description: "Remove a tag from the contact",
    icon: "XMarkIcon",
  },
  {
    value: "wait",
    label: "Wait",
    description: "Wait for a specified duration",
    icon: "ClockIcon",
  },
  {
    value: "add_to_list",
    label: "Add to List",
    description: "Add the contact to a list",
    icon: "UserGroupIcon",
  },
  {
    value: "remove_from_list",
    label: "Remove from List",
    description: "Remove the contact from a list",
    icon: "UserMinusIcon",
  },
  {
    value: "update_attribute",
    label: "Update Attribute",
    description: "Update a contact attribute",
    icon: "PencilSquareIcon",
  },
  {
    value: "create_task",
    label: "Create Task",
    description: "Create a support task for follow-up",
    icon: "ClipboardDocumentListIcon",
  },
];

export const BRANCH_CONDITION_OPTIONS: Array<{
  value: BranchCondition;
  label: string;
  description: string;
  requiresConfig: boolean;
}> = [
  {
    value: "default",
    label: "Default",
    description: "Fallback if no other condition matches",
    requiresConfig: false,
  },
  {
    value: "email_opened",
    label: "Email Opened",
    description: "Contact opened the previous email",
    requiresConfig: false,
  },
  {
    value: "email_not_opened",
    label: "Email Not Opened",
    description: "Contact did not open the previous email",
    requiresConfig: false,
  },
  {
    value: "link_clicked",
    label: "Link Clicked",
    description: "Contact clicked a link in the previous email",
    requiresConfig: false,
  },
  {
    value: "link_not_clicked",
    label: "Link Not Clicked",
    description: "Contact did not click a link",
    requiresConfig: false,
  },
  {
    value: "form_submitted",
    label: "Form Submitted",
    description: "Contact submitted a form",
    requiresConfig: false,
  },
  {
    value: "tag_applied",
    label: "Tag Applied",
    description: "Contact has a specific tag",
    requiresConfig: true,
  },
  {
    value: "tag_not_applied",
    label: "Tag Not Applied",
    description: "Contact does not have a specific tag",
    requiresConfig: true,
  },
  {
    value: "contact_field_equals",
    label: "Field Equals",
    description: "A contact field matches a specific value",
    requiresConfig: true,
  },
  {
    value: "contact_field_not_equals",
    label: "Field Not Equals",
    description: "A contact field does not match a value",
    requiresConfig: true,
  },
  {
    value: "in_segment",
    label: "In Segment",
    description: "Contact is a member of a specific segment/list",
    requiresConfig: true,
  },
  {
    value: "not_in_segment",
    label: "Not In Segment",
    description: "Contact is NOT a member of a specific segment/list",
    requiresConfig: true,
  },
];

export const TRIGGER_TYPE_OPTIONS: Array<{
  value: SequenceTriggerType;
  label: string;
  description: string;
}> = [
  // Order and labels agreed with marketing (owner, 2026-09-30).
  {
    value: "manual",
    label: "Manual",
    description: "Manually enroll contacts through the UI",
  },
  {
    value: "member_expiration",
    label: "Member Expiration",
    description: "Trigger before or after membership expires",
  },
  {
    value: "member_signup",
    label: "New Member",
    description: "A contact becomes a member for the first time",
  },
  {
    value: "product_acquired",
    label: "New Product / Upgrade",
    description: "A member gains an active product, including upgrades",
  },
  {
    value: "reinstated_member",
    label: "Reinstated Member",
    description:
      "A former member (an ended subscription with an end date) becomes active again",
  },
  {
    value: "contact_created",
    label: "New Contact",
    description: "Trigger when any new contact is created",
  },
  // Not offered in the sequence form (filtered out there); kept for labels elsewhere.
  {
    value: "list_membership",
    label: "List Membership",
    description: "Auto-enroll when contact joins a specific list",
  },
  {
    value: "event_registration",
    label: "Event Registration",
    description: "Trigger when contact registers for an event",
  },
  {
    value: "event_attended",
    label: "Event Attended (Check-in)",
    description:
      "Trigger when contact is checked in to the event. Note: any contacts already checked in when this sequence first activates will be auto-enrolled on the next evaluation tick.",
  },
];

/**
 * Which trigger types actually USE `message_sequences.list_id` to decide who gets enrolled.
 *
 * Only ONE does. This is not cosmetic: on 2026-09-14 the "Lifetime Membership Welcome Journey"
 * had the correct 10-person "Lifetime Members" list attached and NO trigger conditions, so it
 * looked scoped and was not. It enrolled 1,465 people in 14 days and emailed them
 * "Congratulations on Your Lifetime Membership".
 *
 * The enrolling code is the `auto_enroll_new_member` trigger, which selects only `id` and
 * `trigger_conditions` from the sequence — it never reads `list_id`. The only database function
 * that consumes a sequence's list is `auto_enroll_gearfire_lead_on_list_add`, i.e. the
 * list-membership path.
 */
export const triggerTypeUsesListForEnrollment = (
  triggerType: SequenceTriggerType,
): boolean => triggerType === "list_membership";

/**
 * Plain-English statement of what each trigger actually enrols on, for UI copy. Written so an
 * operator can tell at a glance that the list is not the control.
 */
export const TRIGGER_TYPE_ENROLLS_ON: Record<SequenceTriggerType, string> = {
  product_acquired:
    "an account gaining the selected active product, including existing-member upgrades",
  manual: "contacts you enroll by hand",
  list_membership: "contacts joining the chosen list",
  member_expiration:
    "a membership expiry date arriving, straight from the member record",
  member_signup:
    "a first-time membership becoming active, straight from the Zuora feed",
  reinstated_member:
    "a former member (ended subscription with an end date) becoming active again, straight from the Zuora feed",
  contact_created: "a new contact record being created",
  event_registration: "someone registering for the event",
  event_attended: "someone being checked in to the event",
};

export const EVALUATION_CADENCE_OPTIONS: Array<{
  value: EvaluationCadence;
  label: string;
  description: string;
}> = [
  {
    value: "manual",
    label: "Manual Only",
    description: "Enroll contacts manually from the UI",
  },
  {
    value: "immediate",
    label: "Immediate",
    description: "Fires instantly when condition changes (e.g., new member)",
  },
  {
    value: "hourly",
    label: "Every Hour",
    description: "Scheduled hourly evaluation",
  },
  {
    value: "daily",
    label: "Daily",
    description: "Scheduled daily evaluation (recommended)",
  },
  {
    value: "weekly",
    label: "Weekly",
    description: "Scheduled weekly evaluation",
  },
];

export const TRIGGER_DATE_FIELD_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: "member_renewal_date", label: "Member Renewal Date" },
  { value: "member_end_date", label: "Member End Date" },
  { value: "member_start_date", label: "Member Start Date" },
  { value: "member_expiration_date", label: "Member Expiration Date" },
  { value: "date_of_birth", label: "Date of Birth" },
  { value: "created_at", label: "Contact Created Date" },
];

export const SEQUENCE_STATUS_OPTIONS: Array<{
  value: SequenceStatus;
  label: string;
  color: string;
}> = [
  { value: "draft", label: "Draft", color: "gray" },
  { value: "active", label: "Active", color: "green" },
  { value: "paused", label: "Paused", color: "yellow" },
  { value: "archived", label: "Archived", color: "red" },
];

export const DELAY_UNIT_OPTIONS: Array<{
  value: DelayUnit;
  label: string;
}> = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
];

export const ENROLLMENT_STATUS_OPTIONS: Array<{
  value: EnrollmentStatus;
  label: string;
  color: string;
}> = [
  { value: "active", label: "Active", color: "blue" },
  { value: "paused", label: "Paused", color: "yellow" },
  { value: "completed", label: "Completed", color: "green" },
  { value: "cancelled", label: "Cancelled", color: "gray" },
  { value: "failed", label: "Failed", color: "red" },
];

export const PHASE_LABELS: Record<EnrollmentPhase, string> = {
  pending_action: "Pending Action",
  waiting_evaluation: "Waiting for Evaluation",
  pending_branch: "Choosing Branch",
  held_evaluation: "Held — Evaluation Error",
};
